import { useMemo, useState } from 'react'
import { faBan } from '@fortawesome/pro-light-svg-icons/faBan'
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash'
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons/faEllipsis'
import Button from '@hotmart/space-lib/dist/components/button'
import { Icon, PopoverMenu } from 'src/components'
import i18n from 'src/i18n'
import { FeatureToggle, useFeatureToggle } from 'src/hook/useFeatureToggle'
import { useUser } from 'src/hook/useUser'
import {
  canUserBanAdmin,
  canUserBanMember,
  canUserBanModerator,
  isAuthorMemberTypeOwnerOrAdmin
} from 'src/utils/user'
import { CommunityMemberPermissionsGroup } from 'src/services/community'
import { OwnerCollectionsPermissionsGroup } from 'src/hook/usePosts'
import { PermissionActions } from 'src/services/posts'
import RemoveComment from 'src/components/Modal/RemoveComment'
import BanMemberModal from 'src/pages/CommunityDetails/Posts/BanMemberModal'
import { useParams } from 'react-router-dom'
import { faEdit } from '@fortawesome/pro-light-svg-icons/faEdit'
import { useDecision } from '@optimizely/react-sdk'
import { DropdownOptionsProps, PathParams } from './dropdownOptions.types'

const DropdownOptions = ({
  comment,
  ownerCollectionId,
  onReloadComments,
  onEditComment
}: DropdownOptionsProps) => {
  const { idPost } = useParams() as PathParams
  const [openPopover, setOpenPopover] = useState(false)
  const [openDeleteCommentModal, setOpenDeleteCommentModal] = useState(false)
  const [openBanMemberModal, setOpenBanMemberModal] = useState(false)
  const [{ enabled: enableEditBadge }] = useDecision('com_eng_web_enable_edit_comment')

  const isBanMemberActive = useFeatureToggle(FeatureToggle.communitiesBanMember)
  const { permissionsUser, hasPermissionToManage, user } = useUser()

  const togglePopoverMenuVisibility = () => setOpenPopover(!openPopover)
  const closePopoverMenu = () => setOpenPopover(false)

  const closeRemoveCommentModal = () => setOpenDeleteCommentModal(false)
  const openRemoveCommentModal = (event: React.MouseEvent) => {
    event.stopPropagation()
    setOpenDeleteCommentModal(true)
  }

  const handleCloseBanMemberModalModal = () => setOpenBanMemberModal(false)
  const handleOpenModalBanMember = (event: React.MouseEvent) => {
    event.stopPropagation()
    setOpenBanMemberModal(true)
  }

  const canEditComment = useMemo(() => {
    if (!enableEditBadge) return false

    const byUcode = comment?.authorUcode === user?.ucode
    const isOwner = permissionsUser?.group === CommunityMemberPermissionsGroup.OWNER
    const isAdmin = permissionsUser?.group === CommunityMemberPermissionsGroup.ADMIN

    return Boolean(byUcode || isOwner || isAdmin)
  }, [enableEditBadge, comment.authorUcode, permissionsUser?.group, user?.ucode])

  const hasPermissionToBanMember = [
    !hasPermissionToManage(permissionsUser?.group as CommunityMemberPermissionsGroup) ||
      comment.authorMemberType === permissionsUser?.group,
    isAuthorMemberTypeOwnerOrAdmin(comment.authorMemberType as OwnerCollectionsPermissionsGroup) &&
      !canUserBanAdmin(permissionsUser?.actions as PermissionActions[]),
    (comment.authorMemberType as CommunityMemberPermissionsGroup) ===
      CommunityMemberPermissionsGroup.MODERATOR &&
      !canUserBanModerator(permissionsUser?.actions as PermissionActions[]),
    !canUserBanMember(permissionsUser?.actions as PermissionActions[])
  ].every((value) => value === false)

  return (
    <div>
      <div className="_d-flex _flex-column _justify-content-center _align-items-end _position-relative">
        <Icon
          className="_cursor-pointer"
          icon={faEllipsis}
          data-test="dropdown-action"
          onClick={togglePopoverMenuVisibility}
        />

        <PopoverMenu open={openPopover} close={closePopoverMenu}>
          {canEditComment && (
            <Button
              variation="tertiary"
              className="_d-flex _align-items-center _rounded-0 _px-4 _py-3"
              data-test="open-edit-modal"
              onClick={(e) => {
                e.stopPropagation()
                onEditComment(comment)
                closePopoverMenu()
              }}
            >
              <Icon icon={faEdit} className="_mr-3" />
              {i18n.t('comments.button_edit')}
            </Button>
          )}

          {isBanMemberActive && hasPermissionToBanMember && (
            <Button
              variation="tertiary"
              className="_d-flex _align-items-center _rounded-0 _px-4 _py-3"
              onClick={handleOpenModalBanMember}
            >
              <Icon icon={faBan} className="_mr-3" /> {i18n.t('buttons.ban_member')}
            </Button>
          )}

          <Button
            variation="tertiary"
            className="_d-flex _align-items-center _rounded-0 _px-4 _py-3"
            data-test="open-remove-modal"
            onClick={openRemoveCommentModal}
          >
            <Icon icon={faTrash} className="_mr-3" />
            {i18n.t('comments.button_delete')}
          </Button>
        </PopoverMenu>
      </div>

      <RemoveComment
        isOpen={openDeleteCommentModal}
        newsId={Number(idPost)}
        commentId={comment.id}
        refreshPage={false}
        setRefreshPage={onReloadComments}
        closeModal={closeRemoveCommentModal}
      />

      {isBanMemberActive && ownerCollectionId && (
        <BanMemberModal
          isOpen={openBanMemberModal}
          userId={comment.authorId}
          collectionId={ownerCollectionId as number}
          userName={comment.author}
          refreshPageReplies={false}
          setRefreshPageReplies={onReloadComments}
          closeModal={handleCloseBanMemberModalModal}
        />
      )}
    </div>
  )
}

export default DropdownOptions
